import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Button from '@mui/material/Button';
import { SpinnerCircular } from 'spinners-react';

import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import useHttp from "../../../hooks/useHttp";
import { BACKEND_HOST } from "../../../config";
import "../../../assets/css/utility/input-field.css";
import "../../../assets/css/user_auth_pages.css";
import Loader from "../../../components/Loader";
import SuccessMessage from "../../../components/SuccessMessage";
import { REGISTER_URL } from "../../../utils/api-ends";

const Registration = () => {
  const [searchParams] = useSearchParams();
  // searchParams.get('type')

  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [userType, setUserType] = useState(searchParams.get('type') || "");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const { error, isLoading, sendRequests } = useHttp();

  const [emailError, setEmailError] = useState([]);
  const [phoneNumberError, setPhoneNumberError] = useState([]);
  const [userTypeError, setUserTypeError] = useState([]);
  const [passwordError, setPasswordError] = useState([]);
  const [passwordConfirmError, setPasswordConfirmError] = useState([]);
  const [errorMessages, setErrorMessages] = useState([]);
  const [successMessage, setSuccessMessage] = useState(null);

  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const navigate = useNavigate();

  const onChangeEmail = (e) => setEmail(e.target.value);
  const onChangePhoneNumber = (e) => setPhoneNumber(e.target.value);
  const onChangeUserType = (e) => setUserType(e.target.value);
  const onChangePassword = (e) => setPassword(e.target.value);
  const onChangePasswordConfirm = (e) => setPasswordConfirm(e.target.value);

  const viewPassword = () => setShowPassword(!showPassword);
  const viewPasswordConfirm = () =>
    setShowPasswordConfirm(!showPasswordConfirm);

  const validateData = () => {
    // Empty error messages
    setEmailError([]);
    setPhoneNumberError([]);
    setUserTypeError([]);
    setPasswordError([]);
    setPasswordConfirmError([]);
    setErrorMessages([]);

    let hasError = false;

    // Validate all fields
    if (!email?.trim() && !phoneNumber.trim()) {
      setErrorMessages(["Please enter your email address or phone number."]);
      hasError = true;
    }
    if (!userType?.trim()) {
      setUserTypeError(["User type is required"]);
      hasError = true;
    }
    if (!password?.trim()) {
      setPasswordError(["Password is required"]);
      hasError = true;
    }
    if (!passwordConfirm?.trim()) {
      setPasswordConfirmError(["Password confirmation is required"]);
      hasError = true;
    }

    if (password !== passwordConfirm) {
      setPasswordError(["Password and password confirmation must match"]);
      setPasswordConfirmError(["Password and password confirmation must match"]);
      hasError = true;
    }

    return hasError;
  };

  const onSubmit = (e) => {
    if(isLoading) return false;
    if (validateData()) return false

    const response = (data) => {
      if (data.email_send_message) {
        setSuccessMessage([data.email_send_message]);
      }
      if (data.phone_send_message) {
        setSuccessMessage([data.sms_send_message]);
      }

      // Add access token and refresh token to local storage
      localStorage.setItem(
        "auth_token",
        JSON.stringify({
          access: data.access,
          refresh: data.refresh,
        })
      );

      // Navigate to verification page
      setTimeout(() => {
        if (data.have_to_verify === "both") {
          navigate("/auth/account-verification?to_verify=both");
        } else if (data.have_to_verify === "email") {
          navigate("/auth/account-verification?to_verify=email");
        } else if (data.have_to_verify === "phone") {
          navigate("/auth/account-verification?to_verify=phone");
        }
      }, 500);
    };

    sendRequests(
      {
        url: REGISTER_URL,
        method: "POST",
        data: {
          email,
          password,
          confirm_password: passwordConfirm,
          phone_number: phoneNumber?.length > 0 ? `+1${phoneNumber}` : phoneNumber,
          user_type: userType,
        },
      },
      response
    );
  };

  // Append server error messages to every field error array
  useEffect(() => {
    if (error) {
      if (error.email) {
        setEmailError(error.email);
      }
      if (error.phone_number) {
        setPhoneNumberError(error.phone_number);
      }
      if (error.user_type) {
        setUserTypeError(error.user_type);
      }
      if (error.password) {
        setPasswordError(error.password);
      }
      if (error.password) {
        setPasswordConfirmError(error.confirm_password);
      }
      if (error.non_field_errors) {
        setErrorMessages(error.non_field_errors);
      }
      if (error.general) {
        setErrorMessages(error.general);
      }
    }
  }, [error]);

  return (
    <>
      <div className="user-input-field">
        <div className={{ marginBottom: "2rem" }}>
          {/* Success messages  */}
          {successMessage?.map((message, index) => (
            <SuccessMessage key={index} message={successMessage} />
          ))}

          {/* Error Messages  */}
          {errorMessages?.map((error, index) => (
            <p className="auth-error" key={index}>
              {error}
            </p>
          ))}
        </div>

        <div className="input-box-area">
          {emailError?.map((error, index) => (
            <p className="auth-error" key={index}>
              {error}
            </p>
          ))}

          <TextField
            fullWidth
            className="the-mui-input-field"
            onChange={onChangeEmail}
            label="Email Address"
            variant="outlined"
          />
        </div>

        <div className="input-box-area">
          {phoneNumberError?.map((error, index) => (
            <p className="auth-error" key={index}>
              {error}
            </p>
          ))}
          <div className="phone-number-input-area">
            <input
              type="text"
              defaultValue="+1"
              disabled="true"
              className="phone-number-input-code-registration"
            />
            <TextField
              fullWidth
              className="the-mui-input-field"
              onChange={onChangePhoneNumber}
              label="Phone Number"
              variant="outlined"
              placeholder="e.g. 6505552671"
            />
          </div>
        </div>

        <div className="input-box-area">
          {userTypeError?.map((error, index) => (
            <p className="auth-error" key={index}>
              {error}
            </p>
          ))}
          <FormControl fullWidth>
            <div>
              <InputLabel>User Type</InputLabel>
              <Select
                value={userType}
                label="User Type"
                onChange={onChangeUserType}
                className="input-box-area-select-field"
                fullWidth
              >
                <MenuItem value="shipper">Shipper</MenuItem>
                <MenuItem value="carrier">Carrier</MenuItem>
              </Select>
            </div>
          </FormControl>
        </div>

        <div className="input-box-area">
          {passwordError?.map((error, index) => (
            <p className="auth-error" key={index}>
              {error}
            </p>
          ))}
          <TextField
            fullWidth
            className="the-mui-input-field"
            onChange={onChangePassword}
            label="Password"
            type={showPassword ? "text" : "password"}
            variant="outlined"
            placeholder="e.g Abad123!Hj"
          />
          <span onClick={viewPassword}>
            <FontAwesomeIcon
              icon={`fa-solid ${showPassword ? "fa-eye" : "fa-eye-slash"}`}
            />
          </span>
        </div>

        <div className="input-box-area">
          {passwordConfirmError?.map((error, index) => (
            <p className="auth-error" key={index}>
              {error}
            </p>
          ))}
          <TextField
            fullWidth
            className="the-mui-input-field"
            onChange={onChangePasswordConfirm}
            label="Confirm Password"
            variant="outlined"
            placeholder="e.g Abad123!Hj"
            type={showPasswordConfirm ? "text" : "password"}
          />
          <span onClick={viewPasswordConfirm}>
            <FontAwesomeIcon
              icon={`fa-solid ${
                showPasswordConfirm ? "fa-eye" : "fa-eye-slash"
              }`}
            />
          </span>
        </div>

        <Button className="theme-btn submit-btn" onClick={onSubmit}>
          Registration
          {
            isLoading &&
            <SpinnerCircular 
              size={22} 
              thickness={180} 
              speed={130} 
              color="#fff" 
              className="btn-loader"
              secondaryColor="rgba(57, 172, 127, 0.44)" 
            />
          }
        </Button>
      </div>

      <Link to="/auth/sign-in" className="auth-usefull-links">
        Sign In
      </Link>

      <Link to="/auth/forget-password" className="auth-usefull-links">
        Forget Password ?
      </Link>
    </>
  );
};

export default Registration;
